// BigSplice 3D Visualization Themes and Constants

import { ThemeConfig, ColorPaletteConfig, LayoutType, PaletteType, ThemeType } from '../types/visualization';
import * as THREE from 'three';

// Theme configurations
export const THEMES: Record<ThemeType, ThemeConfig> = {
  dark: {
    background: '#0a0a0a',
    panelBg: 'rgba(12, 12, 12, 0.98)',
    panelBorder: '#222',
    text: '#e0e0e0',
    textMuted: '#666',
    textDimmed: '#444',
    accent: '#fff',
    buttonBg: '#1a1a1a',
    buttonBorder: '#333',
    inputBg: '#141414',
    fog: '#0a0a0a',
    grid1: '#1a1a2e',
    grid2: '#12121a',
    emissive: '#333333',
  },
  light: {
    background: '#f5f5f7',
    panelBg: 'rgba(255, 255, 255, 0.98)',
    panelBorder: '#ddd',
    text: '#1d1d1f',
    textMuted: '#86868b',
    textDimmed: '#999',
    accent: '#000',
    buttonBg: '#f0f0f0',
    buttonBorder: '#ccc',
    inputBg: '#fafafa',
    fog: '#f5f5f7',
    grid1: '#e0e0e0',
    grid2: '#eaeaea',
    emissive: '#888888',
  },
};

// Color palette options
export const COLOR_PALETTES: Record<PaletteType, ColorPaletteConfig> = {
  fire: {
    name: 'Fire',
    colors: ['#1a0a0a', '#4a1010', '#8b0000', '#dc143c', '#ff4500', '#ff8c00', '#ffd700', '#ffffff'],
    gradient: 'linear-gradient(to right, #1a0a0a, #8b0000, #ff4500, #ffd700, #ffffff)',
  },
  ocean: {
    name: 'Ocean',
    colors: ['#0a0a1a', '#001f3f', '#003366', '#0066cc', '#00b4d8', '#48cae4', '#90e0ef', '#caf0f8'],
    gradient: 'linear-gradient(to right, #0a0a1a, #003366, #00b4d8, #90e0ef, #caf0f8)',
  },
  forest: {
    name: 'Forest',
    colors: ['#0a1a0a', '#1b4332', '#2d6a4f', '#40916c', '#52b788', '#74c69d', '#95d5b2', '#d8f3dc'],
    gradient: 'linear-gradient(to right, #0a1a0a, #2d6a4f, #52b788, #95d5b2, #d8f3dc)',
  },
  sunset: {
    name: 'Sunset',
    colors: ['#1a0a1a', '#2d1b4e', '#5c3d7a', '#9d4edd', '#c77dff', '#e0aaff', '#ff9e9e', '#ffecd2'],
    gradient: 'linear-gradient(to right, #1a0a1a, #5c3d7a, #c77dff, #ff9e9e, #ffecd2)',
  },
  neon: {
    name: 'Neon',
    colors: ['#0d0d0d', '#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff6b6b', '#feca57', '#48dbfb'],
    gradient: 'linear-gradient(to right, #0d0d0d, #0f3460, #e94560, #feca57, #48dbfb)',
  },
  monochrome: {
    name: 'Mono',
    colors: ['#0a0a0a', '#1a1a1a', '#333333', '#4d4d4d', '#808080', '#b3b3b3', '#e6e6e6', '#ffffff'],
    gradient: 'linear-gradient(to right, #0a0a0a, #333333, #808080, #e6e6e6, #ffffff)',
  },
  plasma: {
    name: 'Plasma',
    colors: ['#0d0887', '#46039f', '#7201a8', '#9c179e', '#bd3786', '#d8576b', '#ed7953', '#f0f921'],
    gradient: 'linear-gradient(to right, #0d0887, #7201a8, #bd3786, #ed7953, #f0f921)',
  },
};

// Distinct categorical colors for group-based coloring (maximally different)
export const CATEGORICAL_COLORS = [
  '#e6194b', // red
  '#3cb44b', // green
  '#ffe119', // yellow
  '#4363d8', // blue
  '#f58231', // orange
  '#42d4f4', // cyan
  '#f032e6', // magenta
  '#bfef45', // lime
  '#fabed4', // pink
  '#469990', // teal
  '#dcbeff', // lavender
  '#9a6324', // brown
  '#fffac8', // beige
  '#800000', // maroon
  '#aaffc3', // mint
  '#808000', // olive
  '#ffd8b1', // apricot
  '#000075', // navy
  '#a9a9a9', // gray
  '#ffffff', // white
];

// Layout options
export const LAYOUT_OPTIONS: LayoutType[] = ['bars', 'donut', 'pie', 'force'];

// Shape type options
export const SHAPE_TYPES = ['cube', 'pyramid', 'sphere'] as const;

// Cardinality threshold - above this, use dropdown instead of segmented buttons
export const CARDINALITY_THRESHOLD = 8;

// Threshold for "Others" category - groups with less than this % get merged
export const OTHERS_THRESHOLD = 0.02; // 2%

// Cache for color palettes (THREE.Color objects)
const cachedPaletteColors: Record<string, THREE.Color[]> = {};

export function getCachedPaletteColors(palette: PaletteType): THREE.Color[] {
  if (!cachedPaletteColors[palette]) {
    cachedPaletteColors[palette] = COLOR_PALETTES[palette].colors.map(c => new THREE.Color(c));
  }
  return cachedPaletteColors[palette];
}

// Get color from palette based on normalized value (0-1)
export function getGradientColor(palette: PaletteType, t: number): THREE.Color {
  const colors = getCachedPaletteColors(palette);
  const clampedT = Math.max(0, Math.min(1, t));
  const segments = colors.length - 1;
  const segmentIndex = Math.min(Math.floor(clampedT * segments), segments - 1);
  const segmentT = (clampedT * segments) - segmentIndex;

  const color = new THREE.Color();
  color.lerpColors(colors[segmentIndex], colors[segmentIndex + 1], segmentT);
  return color;
}

// Get min/max for a column
export function getColumnRange(rows: Record<string, any>[], column: string): { min: number; max: number } {
  let min = Infinity, max = -Infinity;
  for (const row of rows) {
    const val = typeof row[column] === 'number' ? row[column] : parseFloat(row[column]) || 0;
    if (val < min) min = val;
    if (val > max) max = val;
  }
  return { min, max };
}

// Check if a row matches the search query
export function rowMatchesSearch(row: Record<string, any>, searchQuery: string): boolean {
  if (!searchQuery) return false;
  const query = searchQuery.toLowerCase();
  return Object.values(row).some(val =>
    String(val).toLowerCase().includes(query)
  );
}

// Get numeric value from a column (handles various formats)
export function getNumericValue(value: any): number {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const str = String(value);

  // Handle percentages like "57% C"
  if (str.includes('%')) {
    const match = str.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  // Handle sizes like "8 GB"
  if (str.includes('GB')) {
    const match = str.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  // Handle IP addresses - convert to numeric
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(str)) {
    return ipToNumber(str);
  }

  // Try parsing as number
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
}

// Parse IP address to numeric value
export function ipToNumber(ip: string): number {
  if (!ip || typeof ip !== 'string') return 0;
  const parts = ip.split('.');
  if (parts.length !== 4) return 0;
  return parts.reduce((acc, part, idx) => {
    const num = parseInt(part, 10) || 0;
    return acc + num * Math.pow(256, 3 - idx);
  }, 0);
}

// Check if mobile device
export function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

// Default view settings
export const DEFAULT_VIEW_SETTINGS = {
  autoRotate: false,
  rotationSpeed: 0.5,
  showGrid: true,
  showStars: false,
  showFog: true,
  animSpeed: 0.5,
};

// Default visualization settings
export const DEFAULT_VISUALIZATION_SETTINGS = {
  layout: 'pie' as LayoutType,
  colorPalette: 'ocean' as PaletteType,
  theme: 'dark' as ThemeType,
  searchHighlightColor: '#ff0000',
  cubeMagnification: 1.8,
  metallicMode: true,
  shapeType: 'cube' as const,
  sizeByDimension: 'y' as const,
  maxSizeMultiplier: 6,
  sortDirection: 'desc' as const,
};
